import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type {
  ComplianceTrendRow,
  DashboardTotals,
  EscalationRow,
  SiteCompliance,
  TodayReport,
} from '../api/types';
import { Badge, Card, Loading, Metric, Notice } from '../components';
import { ComplianceTrendChart, RankedBars } from '../components/Charts';
import { Today } from '../components/Today';
import { useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * What needs attention today.
 *
 * Ordered by what a supervisor can act on rather than by what is easy to count. Escalated
 * answers come before compliance percentages because a bad answer is something to go and
 * look at, and a percentage is a thing to think about later.
 */
/**
 * The monthly series behind a tile, and the change across its last two months.
 *
 * Returns neither below two months: one point cannot be a trend, and a change needs
 * something to have changed from. The tile then shows its number alone, which is the
 * honest rendering of "there is no history yet" — a flat line and a zero delta would
 * both be claims this data cannot support.
 */
function monthly(
  rows: readonly ComplianceTrendRow[] | null,
  field: 'totalSubmissions' | 'acceptedCount' | 'flaggedCount' | 'blockedCount',
): { trend?: readonly number[]; comparedWith?: { latest: number; previous: number; caption: string } } {
  if (rows === null || rows.length < 2) return {};

  const values = rows.map((row) => row[field]);
  const latest = rows[rows.length - 1]!;
  const previous = rows[rows.length - 2]!;

  return {
    trend: values,
    comparedWith: {
      latest: latest[field],
      previous: previous[field],
      caption: `${latest.periodMonth.slice(0, 7)} vs ${previous.periodMonth.slice(0, 7)}`,
    },
  };
}

export function Dashboard(): JSX.Element {
  const { can, config } = useSession();

  const totals = useResource(
    () => (can('report.view') ? api.get<DashboardTotals>('/reports/totals') : Promise.resolve(null)),
    [],
  );
  const queue = useResource(
    () => (can('exception.view') ? api.get<EscalationRow[]>('/escalations?limit=8') : Promise.resolve([])),
    [],
  );
  const sites = useResource(
    () =>
      can('report.view')
        ? api.get<SiteCompliance[]>('/reports/compliance-by-site')
        : Promise.resolve([]),
    [],
  );
  const trend = useResource(
    () =>
      can('report.view')
        ? api.get<ComplianceTrendRow[]>('/reports/compliance-trend')
        : Promise.resolve([]),
    [],
  );
  /**
   * No date sent, so the server answers for its own day.
   *
   * Passing the browser's date would make the panel disagree with the schedule that
   * generated the visits whenever the two are in different timezones — and it is the
   * schedule that decides which day a visit belongs to.
   */
  const today = useResource(
    () => (can('report.view') ? api.get<TodayReport>('/reports/today') : Promise.resolve(null)),
    [],
  );

  return (
    <>
      {totals.error !== null && <Notice kind="error">{totals.error}</Notice>}

      {/*
        Today first, and above the all-time totals.

        The tiles below are cumulative — they answer "how has the estate done", which is a
        thing to think about. This answers "what is happening right now", which is a thing to
        act on, and a supervisor opening the panel mid-afternoon is asking the second.
      */}
      {can('report.view') && (
        <Card
          title="Today"
          actions={<Link to="/schedule">Open the schedule</Link>}
        >
          {today.loading ? (
            <Loading />
          ) : today.error !== null ? (
            <Notice kind="error">{today.error}</Notice>
          ) : (
            today.data !== null && <Today report={today.data} fieldUserLabel={config.fieldUserLabel} />
          )}
        </Card>
      )}

      {totals.loading ? (
        <Loading />
      ) : (
        totals.data !== null && (
          <div className="metrics">
            {/*
              The tile value is the running total; the sparkline and the delta beside it
              describe the months behind it, which is why the caption names both months
              rather than saying "vs last month".

              `upIsGood` is set only where the direction carries a judgement. More accepted
              submissions is good and more blocked ones bad, but a busier month is neither,
              so Submissions gets no colour — inventing one would assert something the
              number does not say.
            */}
            <Metric label="Submissions" value={totals.data.totalSubmissions} {...monthly(trend.data, 'totalSubmissions')} />
            <Metric
              label="Accepted"
              value={totals.data.accepted}
              tone="success"
              upIsGood
              {...monthly(trend.data, 'acceptedCount')}
            />
            <Metric
              label="Flagged"
              value={totals.data.flagged}
              tone="warning"
              upIsGood={false}
              {...monthly(trend.data, 'flaggedCount')}
            />
            <Metric
              label="Blocked"
              value={totals.data.blocked}
              tone="danger"
              upIsGood={false}
              {...monthly(trend.data, 'blockedCount')}
            />
            {/* Only for somebody who could see them. A permanent "0 Escalated" to a store
                owner who has no such permission reads as "nothing is wrong", when what it
                means is "you are not being shown this". */}
            {can('exception.view') && (
              <Metric label="Escalated" value={queue.data?.length ?? 0} tone="warning" />
            )}
          </div>
        )
      )}

      {/*
        What period these cover, said out loud.
        
        The figures are unfiltered — every submission ever recorded — and nothing on the
        screen said so, which leaves a reader to assume a period and read the numbers as a
        month or a quarter. Reports is where a date range lives, so it is named.
      */}
      {totals.data !== null && !totals.loading && (
        <p className="muted" style={{ marginTop: -4, marginBottom: 16 }}>
          Everything recorded so far. For a period, use <Link to="/reports">Reports</Link>.
        </p>
      )}

      {/*
        Two columns from here down.

        Stacked full-width, a supervisor scrolled past the trend and the rankings to reach
        the escalations — the one thing on this screen that is a job rather than a reading.
        Side by side, what to do and what it looks like are in view at once. Below 1080px
        this collapses back to one column, escalations first.
      */}
      <div className="grid-2">
        <div className="grid-2-main">
          {can('report.view') && (
            <Card
              title="Compliance over time"
              actions={<span className="card-note">By month</span>}
            >
              {trend.loading ? (
                <Loading />
              ) : trend.error !== null ? (
                <Notice kind="error">{trend.error}</Notice>
              ) : (
                <ComplianceTrendChart rows={trend.data ?? []} />
              )}
            </Card>
          )}

          {/*
            Ranked rather than tabulated. "Which of these is worst" is a magnitude
            comparison, and a bar answers it at a glance where a column of percentages has
            to be read row by row. The figures stay on the right of each bar, so nothing is
            lost by not being a table.
          */}
          {can('report.view') && (
            <Card
              title={`Weakest ${config.siteLabel.toLowerCase()}s`}
              actions={<Link to="/reports">All reports</Link>}
            >
              {sites.loading ? (
                <Loading />
              ) : sites.error !== null ? (
                <Notice kind="error">{sites.error}</Notice>
              ) : (
                <RankedBars
                  countLabel="submissions"
                  emptyText="No submissions have been recorded yet."
                  items={(sites.data ?? []).slice(0, 8).map((row) => ({
                    key: row.siteId,
                    label: row.siteName,
                    sublabel: row.siteCode,
                    value: row.compliancePercent,
                    count: row.totalSubmissions,
                  }))}
                />
              )}
            </Card>
          )}
        </div>

        <div className="grid-2-side">
          {can('exception.view') && (
            <Card title="Escalated answers" actions={<Link to="/escalations">See all</Link>}>
              {queue.loading ? (
                <Loading />
              ) : queue.error !== null ? (
                <Notice kind="error">{queue.error}</Notice>
              ) : (queue.data ?? []).length === 0 ? (
                <p className="empty">Nothing has been escalated.</p>
              ) : (
                /*
                  A stacked feed rather than a table. As four columns this was clipped the
                  moment it moved into the narrower column — the question, which is the
                  reason the row is here at all, ran off the edge behind a scrollbar.
                */
                <ul className="feed">
                  {(queue.data ?? []).map((row) => (
                    <li className="feed-item" key={row.answerId}>
                      <span className="feed-top">
                        <span className="feed-where">
                          {row.zoneName === null
                            ? `${row.siteCode} — ${row.siteName}`
                            : `${row.siteCode} — ${row.siteName} · ${row.zoneName}`}
                        </span>
                        <span className="feed-when">{row.scheduledDate}</span>
                      </span>
                      <span className="feed-what">{row.prompt}</span>
                      <span className="feed-answer">
                        <Badge tone="flagged">{row.answerLabel}</Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
